/* ============================================================
   SINGSPACE ADMIN — INVOICES PAGE (CLEAN CODE REFACTORED)
   ============================================================ */

import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import { useInvoicesAdmin } from '../../../hooks/useInvoicesAdmin';
import { InvoiceFiltersBar } from './InvoiceFiltersBar';
import { InvoiceStatsCards } from './InvoiceStatsCards';
import { InvoiceTable } from './InvoiceTable';

export function InvoicesPage() {
  const {
    invoices,
    loading,
    error,
    pagination,
    stats,
    setPage,
    statusFilter,
    searchQuery,
    updateFilter,
    updateSearch,
    resetFilters: resetFiltersHook,
    refresh,
  } = useInvoicesAdmin();

  // Les factures sont déjà paginées via le hook
  const displayedInvoices = Array.isArray(invoices) ? invoices : [];

  const hasActiveFilters = statusFilter !== 'all' || searchQuery.trim() !== '';

  const handleInvoiceDeleted = () => {
    refresh();
  };

  // Premier chargement : afficher un spinner plein écran
  if (loading && displayedInvoices.length === 0) {
    return (
      <div className="space-y-6 pb-12">
        <PageHeader />
        <LoadingSpinner message="Chargement des factures..." submessage="Veuillez patienter" size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 pb-12">
        <PageHeader />
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 font-medium mb-2">Erreur lors du chargement</p>
          <p className="text-red-500 text-sm">{String(error)}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12">
      <PageHeader />

      <InvoiceStatsCards stats={stats} />

      <InvoiceFiltersBar
        searchQuery={searchQuery}
        statusFilter={statusFilter}
        totalResults={pagination.total}
        filteredResults={pagination.total}
        hasActiveFilters={hasActiveFilters}
        onSearchChange={updateSearch}
        onStatusChange={(value) => updateFilter('status', value)}
        onResetFilters={resetFiltersHook}
      />

      <div className="relative">
        <InvoiceTable
          invoices={displayedInvoices}
          onResetFilters={resetFiltersHook}
          onInvoiceDeleted={handleInvoiceDeleted}
        />

        {/* Overlay de chargement pour les changements de page/filtres (isFetching = background refetch) */}
        {loading && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center rounded-xl z-10">
            <LoadingSpinner message="Chargement..." submessage="" size="sm" />
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl px-6 py-4">
          <div className="text-sm text-slate-600">
            Page {pagination.page} sur {pagination.totalPages} • {pagination.total} facture(s) au total
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Précédent
            </button>
            <button
              onClick={() => setPage(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="px-4 py-2 text-sm font-medium text-white bg-[#00758d] border border-[#00758d] rounded-lg hover:bg-[#006374] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PageHeader() {
  return (
    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Facturation</h1>
        <p className="text-slate-500 mt-1">Gestion des factures pro forma pour les réservations payées</p>
      </div>
    </div>
  );
}

export default InvoicesPage;
